import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Actions
import { openViewMediaImagesForm } from "../../actions/media-selection/MediaSelectionActions";

// Constants and Utils
import { formatText } from "../../../common-utils/string-utils/StringUtils";
import {
  toFixedIfDecimalNumber,
  toStringWithRoundUp,
} from "../../../common-utils/number-utils/NumberUtils";
import { getMediaCostForCampaign } from "../../../utils/MediaUtils";
import {
  getDifferenceInDays,
  getTimestamp,
} from "../../../common-utils/date-utils/DateUtils";
import { DATE_FORMATS } from "../../../constants/GeneralConstants";
import {
  ToohlLitStatusValues,
  ProohFormDataTargets,
} from "../../constants/GeneralConstants";

// Components
import BootstrapDateRangePicker from "../../../components/bootstrap-date-range-picker/BootstrapDateRangePicker";
import Spinner from "../../../components/spinner/Spinner";

// Images
import toohlDefaultMediaImage from "../../../assets/imgs/prooh/dgtoohl_default_media.jpeg";

// Section Component Cells
export function MediaTag({ media = {}, tag }) {
  const { mediaId, sourceId } = media || {};

  const isMediaSelected = useSelector(
    (state) => state.mediaSelection.selectedMedia[mediaId]
  );

  const tagBgColor = isMediaSelected ? "bg-success" : "bg-danger";

  return (
    <div className="d-flex align-items-center justify-content-between">
      <div className={`media-tag mr-2 text-center ${tagBgColor}`}>
        <b className="text-white">{tag}</b>
      </div>
      <p className="mb-0" title={sourceId}>
        Device ID : {sourceId || "--"}
      </p>
    </div>
  );
}

export function MediaImageSection({ media = {}, className = "" }) {
  const dispatch = useDispatch();

  const [isImageError, setIsImageError] = useState(false);

  const { fileItems = [] } = media || {};
  const { thumbnailUrl } = fileItems[0] || {};

  // default image of dgtoohl media
  if (!thumbnailUrl) {
    return (
      <img
        className={`w-100 ${className}`}
        src={toohlDefaultMediaImage}
        alt="default-media-image"
      />
    );
  }

  function handleImageError(event) {
    const { target } = event;

    // updating image error and src
    target.onerror = null; // Prevents infinite loop
    target.src = toohlDefaultMediaImage;

    // update state for error msg
    setIsImageError(true);
  }

  function handleShowMediaImages() {
    // do nothing if image broken
    if (isImageError) {
      return;
    }

    // open modal
    dispatch(openViewMediaImagesForm({ media }));
  }

  return (
    <div className="w-100 d-flex flex-column align-items-center">
      <img
        className={`w-100 cursor-pointer ${className}`}
        src={thumbnailUrl}
        alt="thumbnail-image"
        data-toggle="modal"
        data-target={`#${ProohFormDataTargets.viewMediaImagesFrom}`}
        onClick={handleShowMediaImages}
        onError={handleImageError}
      />

      {/* error fallback msg */}
      {isImageError && (
        <h5 className="text-danger mb-0 mt-n4 font-weight-bold">
          Image Error Fallback
        </h5>
      )}
    </div>
  );
}

function MediaDimensions({ height, width }) {
  return <p className="mb-0">{`Height: ${height} ft | Width: ${width} ft`}</p>;
}

function MediaTitle({ title = "" }) {
  return (
    <h6 className="text-primary text-truncate-2" title={title}>
      {title}
    </h6>
  );
}

function MediaMetaData({ media = {} }) {
  const { title, type, height, width, litStatus, region } = media || {};
  const litStatusString = ToohlLitStatusValues[litStatus];

  return (
    <div className="my-2">
      <MediaTitle title={title} />

      {/* meta data */}
      <p className="mb-0">
        {type} {litStatus ? `| ${litStatusString}` : ""}
      </p>
      <p className="mb-0">Region: {region ? region : ""}</p>
      <MediaDimensions height={height} width={width} />
    </div>
  );
}

function AvailableDateAndOtsAndCPICell({
  media = {},
  duration,
  // setDuration,
  mediaCpai,
  roadStretchOtsSplit = {},
}) {
  const { pricing = {}, suggestedPrice = {} } = media || {};

  // stretch impressions (generic)
  const { ots, otsLit } = roadStretchOtsSplit || {};
  const finalOts = otsLit ?? ots;
  const impressions = finalOts ? finalOts : 0;
  const formattedImpressions =
    impressions === 0 ? 0 : formatText(toStringWithRoundUp(impressions));

  const startTs = getTimestamp(
    duration.startDate,
    DATE_FORMATS.date_month_year
  );
  const endTs = getTimestamp(duration.endDate, DATE_FORMATS.date_month_year);
  const dayCount = getDifferenceInDays(startTs, endTs) - 1;

  // calculate "MOP" from pricing
  const costForCampaign = getMediaCostForCampaign(pricing, dayCount);
  const formattedMop = costForCampaign
    ? toStringWithRoundUp(costForCampaign)
    : 0;

  // calculate suggested price
  const suggPriceForCampaign = getMediaCostForCampaign(
    suggestedPrice,
    dayCount
  );
  const suggPrice = suggPriceForCampaign
    ? toStringWithRoundUp(suggPriceForCampaign)
    : 0;

  // function onApplyDates(selectedDate) {
  //   // .toDate() will retrieve the dateObject from "moment"
  //   const startDate = selectedDate.startDate.toDate();
  //   const endDate = selectedDate.endDate.toDate();
  //   setDuration({ startDate, endDate });
  // }

  // DateRangePicker Style
  const styleObject = {
    border: false,
    isPickerDisabled: true,
    calenderIcon: false,
    placeholderTextStyle: "px-0",
  };

  // data
  const mediaData = [
    { label: "Impressions", value: formattedImpressions },
    {
      label: "CPAI",
      value: mediaCpai,
      title: "Cost Per Audience Impressions",
    },
    { label: "MOP", value: formattedMop },
    { label: "Suggested Price", value: `${suggPrice} Rs` },
  ];

  return (
    <div className="my-2">
      {/* Imp & Cpm */}
      <div className="row mx-0">
        {mediaData.map((data, i) => {
          const { label, value, title } = data;
          return (
            <div key={i} className="col-6 pl-0 mb-2">
              <b title={title}>{label}</b>
              <p className="mb-0">{value}</p>
            </div>
          );
        })}
      </div>

      {/* date */}
      <div className="mt-2">
        <b className="mb-0">Available Dates</b>
        <div>
          <BootstrapDateRangePicker
            initialStartDate={duration.startDate}
            initialEndDate={duration.endDate}
            // onApplyDates={onApplyDates}
            minDate={new Date()}
            styleObject={styleObject}
          />
        </div>
      </div>
    </div>
  );
}

// Non DOOH Media Info
export function NonDOOHMediaInfo({
  media = {},
  duration = {},
  setDuration = () => {},
  mediaCpai,
  roadStretchOtsSplit = {},
}) {
  return (
    <>
      <MediaMetaData media={media} />
      <AvailableDateAndOtsAndCPICell
        media={media}
        duration={duration}
        setDuration={setDuration}
        mediaCpai={mediaCpai}
        roadStretchOtsSplit={roadStretchOtsSplit}
      />
    </>
  );
}

function TrafficInfo({ media = {} }) {
  const { digitalMediaInfo } = media || {};
  const { maxTraffic = "" } = digitalMediaInfo || {};

  return (
    <p className="mb-0 mt-2">
      <b>Max Traffic:</b> {maxTraffic ? maxTraffic : "--"}
    </p>
  );
}

function checkIsResolutionPresent(resolution) {
  if (!resolution) {
    return false;
  }

  if (resolution === "0") {
    return false;
  }

  return true;
}

function DOOHMediaMetaData({ media = {} }) {
  const { title, height, width, digitalMediaInfo = {} } = media || {};
  const { resolution, screenType } = digitalMediaInfo || {};

  const isResolutionPresent = checkIsResolutionPresent(resolution);

  return (
    <div className="my-2">
      <MediaTitle title={title} />

      {/* dooh meta data */}
      <p className="mb-0">{`${screenType ? screenType : "--"} | Digital`}</p>
      <div>
        {!isResolutionPresent && (
          <MediaDimensions height={height} width={width} />
        )}

        {isResolutionPresent && (
          <p className="mb-0">{`Resolution: ${resolution}`}</p>
        )}
      </div>
    </div>
  );
}

function SpotInput({
  noOfSpotsPerSlot,
  setNoOfSpotsPerSlot = () => {},
  isMediaSelected = false,
}) {
  function handleSpotChange(e) {
    const { value } = e.target;

    // default as 1
    if (!value) {
      setNoOfSpotsPerSlot(1);
      return;
    }

    // TODO :: logic for setting max === 18
    // if (value > 18) {
    //   setNoOfSpotsPerSlot(18);
    //   return;
    // }

    // Parse to integer and check if it's valid
    const intValue = parseInt(value, 10);
    if (!isNaN(intValue) && intValue >= 1) {
      setNoOfSpotsPerSlot(intValue);
    }
  }

  return (
    <div className="d-flex align-items-center mt-1">
      <p className="mb-0">
        <b className="mr-2">Add Conventional Buy Spot:</b>
        {/* <small className="d-block">(min: 1, max: 18)</small> */}
      </p>
      <input
        type="number"
        className="form-control shadow-none w-25"
        min={1}
        // max={18} // for max
        value={noOfSpotsPerSlot}
        onChange={handleSpotChange}
        disabled={isMediaSelected}
      />
    </div>
  );
}

function DOOHMediaData({ media = {}, dayCount, mediaCpai }) {
  const { digitalMediaInfo, pricing = {} } = media || {};
  const {
    loopTiming = "",
    slotTiming = "",
    slotDetails = "", // slot timing/loop timing
    costPerSpot = "",
    spotsPerDay = "",
    slotPerMonth = "",
    dailyHoursOfLoop = "",
    // costPerAudienceImp = "",
  } = digitalMediaInfo || {};

  // calculate "MOP" from pricing
  const costForCampaign = getMediaCostForCampaign(pricing, dayCount);
  const formattedMop = costForCampaign
    ? toStringWithRoundUp(costForCampaign)
    : 0;

  // toFixed cost values
  const costPerSpotToFixed = costPerSpot ? Number(costPerSpot) : 0;

  // add min, sec to loopTime and SlotTime
  const finalLoopTime = loopTiming ? `${loopTiming} Min` : "";
  const finalSlotTime = slotTiming ? `${slotTiming} Sec` : "";

  // media data
  const mediaData = [
    { label: "Loop Timing", value: finalLoopTime },
    { label: "Slot Timing", value: finalSlotTime },
    { label: "Slot Details", value: slotDetails },
    { label: "Spots/Day", value: spotsPerDay },
    { label: "Spots/Month", value: slotPerMonth },
    { label: "Daily Hours of Ops", value: dailyHoursOfLoop },
    { label: "MOP", value: formattedMop },
    { label: "Cost/Spot", value: toFixedIfDecimalNumber(costPerSpotToFixed) },
    {
      label: "CPAI",
      value: mediaCpai,
      title: "Cost Per Audience Impressions",
    },
  ];

  return (
    <div className="d-flex flex-wrap justify-content-between">
      {mediaData.map((data, i) => {
        const { label, value, title } = data;
        return (
          <div key={i} className="col-4 pl-0 my-1">
            <b className="mb-0" title={title}>
              {label}
            </b>
            <p className="mb-0">{value === 0 ? 0 : formatText(value)}</p>
          </div>
        );
      })}
    </div>
  );
}

export function DOOHMediaInfo({
  media = {},
  duration,
  isMediaSelected = false,
  noOfSpotsPerSlot,
  setNoOfSpotsPerSlot = () => {},
  mediaCpai,
}) {
  // MOP
  const startTs = getTimestamp(
    duration.startDate,
    DATE_FORMATS.date_month_year
  );
  const endTs = getTimestamp(duration.endDate, DATE_FORMATS.date_month_year);
  const dayCount = getDifferenceInDays(startTs, endTs) - 1;

  return (
    <>
      <DOOHMediaMetaData media={media} />

      {/* DOOH data */}
      <DOOHMediaData media={media} dayCount={dayCount} mediaCpai={mediaCpai} />

      {/* Traffic Details */}
      <TrafficInfo media={media} />

      {/* Spots */}
      <SpotInput
        noOfSpotsPerSlot={noOfSpotsPerSlot}
        setNoOfSpotsPerSlot={setNoOfSpotsPerSlot}
        isMediaSelected={isMediaSelected}
      />
    </>
  );
}

export function AdditionalButtonState({
  isMediaSelected = false,
  selectMedia = () => {},
  unSelectMedia = () => {},
  selectUnSelectMediaLoading = false,
  disabled = false,
}) {
  const btnClassName = isMediaSelected
    ? "added-button btn-outline-success"
    : "add-button btn-outline-primary";

  const onClickFn = isMediaSelected ? unSelectMedia : selectMedia;
  const btnLabel = isMediaSelected ? "Added" : "Add";

  return (
    <button
      type="button"
      className={`btn shadow-none mt-4 ${btnClassName}`}
      onClick={onClickFn}
      disabled={selectUnSelectMediaLoading || disabled}
    >
      {/* Icon */}
      {isMediaSelected && <i className="fa fa-check mr-1"></i>}

      {/* Label */}
      <b>{btnLabel}</b>

      {/* Loading */}
      {!isMediaSelected && selectUnSelectMediaLoading && (
        <Spinner className="ml-2 spinner-border-sm" />
      )}
    </button>
  );
}
